/**
 * ExportButton Component
 * Button to trigger CSV export of filtered orders
 * Shows order count and provides visual feedback during export
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-006 Phase 4 (AC-8)
 */

import * as React from 'react';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { exportOrdersToCSV } from '../utils/csvExport';
import type { OrderSearchResultItem, OrderFilters } from '../types/filter.types';
import { toast } from '@/lib/toast';

// ============================================
// INTERFACE
// ============================================

export interface ExportButtonProps {
  /** Orders to export (should be filtered results) */
  orders: OrderSearchResultItem[];
  /** Current filters (for export info/logging) */
  filters: OrderFilters;
  /** Optional className for styling */
  className?: string;
}

// ============================================
// COMPONENT
// ============================================

export const ExportButton = React.memo(function ExportButton({
  orders,
  filters,
  className,
}: ExportButtonProps) {
  const [isExporting, setIsExporting] = React.useState(false);

  // Handle export click
  const handleExport = React.useCallback(async () => {
    if (!orders || orders.length === 0) {
      toast({
        variant: 'destructive',
        title: 'No Orders to Export',
        description: 'There are no orders matching your current filters.',
      });
      return;
    }

    try {
      setIsExporting(true);

      // Small delay to show loading state for better UX
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Export to CSV
      exportOrdersToCSV(orders);

      // Show success message
      toast({
        title: 'Export Successful',
        description: `Successfully exported ${orders.length} ${
          orders.length === 1 ? 'order' : 'orders'
        } to CSV.`,
      });

      // Log export for analytics (optional)
      console.log('CSV Export:', {
        orderCount: orders.length,
        filters: {
          searchTerm: filters.searchTerm,
          statuses: filters.statuses,
          customerType: filters.customerType,
          location: filters.location,
          hasDateFilter:
            !!filters.orderDateRange.from || !!filters.orderDateRange.to,
          hasAmountFilter:
            filters.amountRange.min !== null ||
            filters.amountRange.max !== null,
        },
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        variant: 'destructive',
        title: 'Export Failed',
        description:
          error instanceof Error
            ? error.message
            : 'An error occurred while exporting orders. Please try again.',
      });
    } finally {
      setIsExporting(false);
    }
  }, [orders, filters]);

  // Check if export is disabled
  const isDisabled = !orders || orders.length === 0 || isExporting;

  // Build tooltip text
  const tooltipText = React.useMemo(() => {
    if (!orders || orders.length === 0) {
      return 'No orders to export';
    }
    return `Download ${orders.length} ${
      orders.length === 1 ? 'order' : 'orders'
    } as CSV`;
  }, [orders]);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={isDisabled}
            className={cn('gap-2', className)}
            aria-label={tooltipText}
          >
            {isExporting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                <span className="hidden sm:inline">Exporting...</span>
              </>
            ) : (
              <>
                <Download className="h-4 w-4" aria-hidden="true" />
                <span className="hidden sm:inline">Export to CSV</span>
                <span className="sm:hidden">Export</span>
                {orders && orders.length > 0 && (
                  <span className="ml-1 rounded bg-muted px-1.5 py-0.5 text-xs font-medium">
                    {orders.length}
                  </span>
                )}
              </>
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-sm">{tooltipText}</p>
        </TooltipContent>
      </Tooltip>

      {/* Accessibility announcement */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {isExporting
          ? 'Exporting orders to CSV...'
          : orders && orders.length > 0
          ? `${orders.length} ${
              orders.length === 1 ? 'order' : 'orders'
            } ready to export`
          : 'No orders to export'}
      </div>
    </TooltipProvider>
  );
});
